import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getProductsByID,
  deleteProduct,
  fetchProducts,
} from "../../../redux/productSlice.js";
import deleteIcon from "../../../assets/images/trash.svg";
import editIcon from "../../../assets/images/editIcon.svg";
import WarningShipmentModalComponent from "../../../components/Modals/WarningShipmentModalComponent.js";
import SuccessModal from "../../../components/Modals/SuccessfulModal.js";
import { fetchCategories } from "../../../redux/categoriesSlice.js";

const ViewProduct = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { productId } = useParams();
  const { t } = useTranslation();
  const location = useLocation();
  const categoryData = useSelector((state) => state.categories.categoriesData);
  const productDetails = useSelector(
    (state) => state.products.getProductData || {}
  );

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  // Fetch product details when component mounts
  useEffect(() => {
    if (productId) {
      dispatch(getProductsByID({ productId }));
    }
    if (categoryData.length <= 0) dispatch(fetchCategories());
  }, [dispatch, productId]);

  const handleEditProductBtnClick = () => {
    navigate(`/edit-products/${productId}`, {
      state: { actionType: "edit", productId: productId, productDetails },
    });
  };

  const handledDeleteProductBtnClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handledDelete = async () => {
    const queryParams = {
      searchText: "",
      pageSize: 9, // Use state value
      pageNumber: 1, // Use state value
    };
    const id = productDetails?.productId;

    await dispatch(deleteProduct({ productId: id }));
    setIsDeleted(true);
    dispatch(fetchProducts(queryParams));
  };

  const onDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  return (
    <>
      <div className="container my-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="fprimary">{t("Review Product Details")}</h1>
          <div>
            <button
              className="btn btn-secondary mx-2"
              onClick={handleEditProductBtnClick}
            >
              <img src={editIcon} alt="Edit" width="16" /> {t("Edit")}
            </button>
            <button
              className="btn btn-danger"
              onClick={handledDeleteProductBtnClick}
            >
              <img src={deleteIcon} alt="Delete" width="16" /> {t("Delete")}
            </button>
          </div>
        </div>

        {/* Basic Information */}
        <div className="card p-4 mb-4">
          <h5 className="mb-3">{t("Basic Information")}</h5>
          <div className="row">
            <div className="col-md-4">
              <p>
                <strong>{t("Product Name")}:</strong>{" "}
                {productDetails.productName || "-"}
              </p>
            </div>
            <div className="col-md-4">
              <p>
                <strong>{t("Weight")}:</strong> {productDetails.weight || "-"}
              </p>
            </div>
            <div className="col-md-4">
              <p>
                <strong>{t("Quantity")}:</strong>{" "}
                {productDetails.quantity || "-"}
              </p>
            </div>
            <div className="col-md-4">
              <p>
                <strong>{t("Price")}:</strong> {productDetails.amount || "-"}
              </p>
            </div>
            <div className="col-md-4">
              <p>
                <strong>{t("Discounted Price")}:</strong>{" "}
                {productDetails.discountedPrice || "-"}
              </p>
            </div>
            <div className="col-md-4">
              <p>
                <strong>{t("Active Status")}:</strong>{" "}
                {productDetails.isActive ? "Active" : "Inactive"}
              </p>
            </div>
          </div>
        </div>

        {/* Category Information */}
        <div className="card p-4 mb-4">
          <h5 className="mb-3">{t("Category")}</h5>
          <div className="bg-slash font-14">
            {productDetails.categoryId ? (
              (() => {
                const category = categoryData.find(
                  (cat) => cat._id === productDetails.categoryId
                );
                return (
                  <p>{category ? category.name : t("Category Not Found")}</p>
                );
              })()
            ) : (
              <p>{t("No Category Available")}</p>
            )}
          </div>
        </div>

        {/* Product Descriptions */}
        <div className="card p-4 mb-4">
          <h5 className="mb-3">{t("Product Descriptions")}</h5>
          {productDetails.productDescription &&
          productDetails.productDescription.length > 0 ? (
            <ul>
              {productDetails.productDescription.map((desc, idx) => (
                <li key={idx}>{desc}</li>
              ))}
            </ul>
          ) : (
            <p>{t("No Description Available")}</p>
          )}
        </div>

        {/* Product Description Languages */}
        <div className="card p-4 mb-4">
          <h5 className="mb-3">{t("Product Description Languages")}</h5>
          {productDetails.productDescriptionLanguages &&
          productDetails.productDescriptionLanguages.length > 0 ? (
            <ul>
              {productDetails.productDescriptionLanguages.map(
                (descLang, idx) => (
                  <li key={idx}>
                    {t(`Language`)} ({descLang.language}): {descLang.value}
                  </li>
                )
              )}
            </ul>
          ) : (
            <p>{t("No Description Languages Available")}</p>
          )}
        </div>

        {/* Product Images */}
        <div className="card p-4 mb-4">
          <h5 className="mb-3">{t("Product Images")}</h5>
          <div className="d-flex flex-wrap">
            {productDetails.productImages &&
            productDetails.productImages.length > 0 ? (
              productDetails.productImages.map((image, idx) => (
                <img
                  key={idx}
                  src={image}
                  alt="Product"
                  className="img-thumbnail m-2"
                  style={{ width: "100px", height: "100px" }}
                />
              ))
            ) : (
              <p>{t("No Images Available")}</p>
            )}
          </div>
        </div>

        {/* Product Languages */}
        <div className="card p-4 mb-4">
          <h5 className="mb-3">{t("Languages")}</h5>
          {productDetails.productLanguages &&
          productDetails.productLanguages.length > 0 ? (
            <ul>
              {productDetails.productLanguages.map((lang, idx) => (
                <li key={idx}>
                  {t(`Language`)} ({lang.language}): {lang.value}
                </li>
              ))}
            </ul>
          ) : (
            <p>{t("No Language Information Available")}</p>
          )}
        </div>

        {/* Product Cover Image */}
        <div className="card p-4 mb-4">
          <h5 className="mb-3">{t("Cover Image")}</h5>
          {productDetails.productCoverImage ? (
            <img
              src={productDetails.productCoverImage}
              alt="Product Cover"
              className="img-thumbnail"
              style={{ width: "150px", height: "150px" }}
            />
          ) : (
            <p>{t("No Cover Image Available")}</p>
          )}
        </div>

        {/* Created At */}
        <div className="card p-4">
          <h5 className="mb-3">{t("Created At")}</h5>
          <p>
            {new Date(productDetails.createdAt).toLocaleDateString() || "-"}
          </p>
        </div>

        <div className="mt-4">
          <button className="btn btn-light" onClick={() => navigate(-1)}>
            {t("Back")}
          </button>
        </div>
      </div>

      {isDeleteModalOpen && (
        <WarningShipmentModalComponent
          isOpen={isDeleteModalOpen}
          onClose={onDeleteModalClose}
          handleDelete={handledDelete}
        />
      )}
      {isDeleted && (
        <SuccessModal
          isOpen={isDeleted}
          msg={t("Product Deleted Successfully")}
          navigatePage="/products"
        />
      )}
    </>
  );
};

export default ViewProduct;
